import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box, Heading, Text, Flex, Button, Select, SimpleGrid,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import LoadingState from '../components/ui/LoadingState';
import EmptyState from '../components/ui/EmptyState';
import Page from '../components/ui/Page';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import SummaryCard from '../components/SummaryCard';
import HelpTooltip from '../components/ui/HelpTooltip';
import TestGroupCard from '../components/TestGroupCard';
import SearchInput from '../components/filters/SearchInput';
import client from '../utils/fetchClient';
import { useProjectContext } from '../context/ProjectContext';

const CARDS_PER_PAGE = 9;
const PERIODS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

// Derive a stable group identifier from a module path, e.g.
// "api/payroll/run.spec.js" -> "payroll", "Payroll" -> "Payroll".
function deriveGroup(modulePath) {
  if (!modulePath) return 'Unknown';
  const parts = String(modulePath).split('/');
  if (parts.length >= 3) return parts[2].replace(/\.spec\.(js|ts)$/, '');
  if (parts.length === 2) return parts[1];
  return parts[0];
}

export default function TestCaseGroupsPage() {
  const navigate = useNavigate();
  const { projects, selectedProject, setSelectedProject, effectiveProjectParam } = useProjectContext();

  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('weekly');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const fetchModules = useCallback(async () => {
    setLoading(true);
    const params = { period, pageSize: 200 };
    if (effectiveProjectParam) params.project = effectiveProjectParam;
    try {
      const res = await client.get('/api/modules', { query: params });
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data?.data || data?.items || data?.results || []);
      setModules(list);
    } catch {
      setModules([]);
    } finally {
      setLoading(false);
    }
  }, [period, effectiveProjectParam]);

  useEffect(() => { fetchModules(); }, [fetchModules]);
  useEffect(() => { setCurrentPage(1); }, [search, period]);

  // The modules endpoint can emit several rows per suite; fold them into one
  // card per derived group and re-total the counts.
  const groups = useMemo(() => {
    const acc = new Map();
    (Array.isArray(modules) ? modules : []).forEach((m) => {
      const name = deriveGroup(m.module);
      const g = acc.get(name) || { name, total: 0, passed: 0, failed: 0, skipped: 0 };
      g.total += Number(m.testCount) || 0;
      g.passed += Number(m.passedCount) || 0;
      g.failed += Number(m.failedCount) || 0;
      g.skipped += Number(m.skippedCount) || 0;
      acc.set(name, g);
    });
    return [...acc.values()]
      .map((g) => ({ ...g, passRate: g.total ? (g.passed / g.total) * 100 : 0 }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [modules]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? groups.filter((g) => g.name.toLowerCase().includes(q)) : groups;
  }, [groups, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / CARDS_PER_PAGE));
  const pageItems = filtered.slice((currentPage - 1) * CARDS_PER_PAGE, currentPage * CARDS_PER_PAGE);

  const summary = useMemo(() => {
    const totalTests = groups.reduce((s, g) => s + g.total, 0);
    const totalPassed = groups.reduce((s, g) => s + g.passed, 0);
    const totalFailed = groups.reduce((s, g) => s + g.failed, 0);
    const passRate = totalTests ? ((totalPassed / totalTests) * 100).toFixed(1) : '0.0';
    const failRate = totalTests ? ((totalFailed / totalTests) * 100).toFixed(1) : '0.0';
    return { totalGroups: groups.length, totalTests, passRate, failRate };
  }, [groups]);

  if (loading) {
    return <LoadingState title="Loading test groups" subtitle="Grouping test cases by module" minH="70vh" showSkeleton />;
  }

  return (
    <Page maxW="7xl">
      <Breadcrumbs items={[{ label: 'Metrics', to: '/metrics' }, { label: 'Test groups' }]} />
      <Box mb={5}>
        <Heading size="lg" fontWeight="800" letterSpacing="-0.5px" color="gray.900" display="flex" alignItems="center">
          Test groups
          <HelpTooltip label="Test cases grouped by module. Open a group to see its cases, or view its run history." href="https://docs.example.com/qa/test-groups" />
        </Heading>
        <Text fontSize="sm" color="gray.500">Coverage and pass rate per module for the selected project</Text>
      </Box>

      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
        <SummaryCard label="Groups" value={summary.totalGroups} tooltipText="Distinct test groups." />
        <SummaryCard label="Test cases" value={summary.totalTests} tooltipText="Total test cases across all groups." />
        <SummaryCard label="Pass rate" value={`${summary.passRate}%`} trend={Number(summary.passRate) >= 80 ? 'up' : 'down'} tooltipText="Passed cases / total cases." />
        <SummaryCard label="Fail rate" value={`${summary.failRate}%`} trend={Number(summary.failRate) <= 10 ? 'up' : 'down'} tooltipText="Failed cases / total cases." />
      </SimpleGrid>

      <Flex align="center" gap={3} wrap="wrap" mb={5}>
        <SearchInput
          value={search}
          onChange={setSearch}
          onClear={() => setSearch('')}
          placeholder="Search groups"
          size="sm"
          flex="1"
        />
        <Select value={period} onChange={(e) => setPeriod(e.target.value)} size="sm" borderRadius="full" maxW="140px" bg="white">
          {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </Select>
        <Select
          display={{ base: 'block', md: 'none' }}
          value={selectedProject || 'ALL PROJECTS'}
          onChange={(e) => setSelectedProject(e.target.value)}
          size="sm"
          borderRadius="full"
          maxW="200px"
          bg="white"
        >
          <option value="ALL PROJECTS">All Projects</option>
          {projects.map((p, idx) => (
            <option key={(p.id ?? p) + '-' + idx} value={p.id ?? p}>{p.name ?? p}</option>
          ))}
        </Select>
        <Box flex="1" />
        <Text fontSize="xs" color="gray.400">{filtered.length} groups</Text>
      </Flex>

      {filtered.length === 0 ? (
        <EmptyState emoji="🗂️" title="No test groups found" subtitle="Try a different search or time range." />
      ) : (
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={5}>
          {pageItems.map((g) => (
            <TestGroupCard
              key={g.name}
              name={g.name}
              metrics={g}
              onView={() => navigate(`/test-cases/${encodeURIComponent(g.name)}`)}
              onHistory={() => navigate(`/test-groups/${encodeURIComponent(g.name)}/history`)}
            />
          ))}
        </SimpleGrid>
      )}

      {totalPages > 1 && (
        <Flex mt={6} align="center" justify="center" gap={4}>
          <Button size="sm" variant="outline" borderRadius="full" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} isDisabled={currentPage === 1}>
            Previous
          </Button>
          <Text fontSize="sm" color="gray.500">Page {currentPage} of {totalPages}</Text>
          <Button size="sm" borderRadius="full" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} isDisabled={currentPage >= totalPages}>
            Next
          </Button>
        </Flex>
      )}
    </Page>
  );
}
